import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { LiquidationAttributes } from '../../interfaces/liquidation.interface';
import { OrderStatusERPEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-status-erp.enum';

/**
 * Liquidation Status Cell Renderer
 *
 * Displays liquidation status with:
 * - Colored circle indicator
 * - Status badge text
 *
 * Status mapping:
 * - 0 → Pendiente (Yellow)
 * - 3 → Sincronizado (Green)
 * - Other → En Proceso (Blue)
 */
@Component({
  selector: 'app-liquidation-status-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex items-center justify-center gap-2">
      <div class="w-2.5 h-2.5 rounded-full" [ngClass]="circleClass"></div>
      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            [ngClass]="badgeClass">
        {{ statusText }}
      </span>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiquidationStatusCellRendererComponent implements CellRenderer<LiquidationAttributes> {
  @Input() params!: CellRendererParams<LiquidationAttributes>;

  get statusERP(): number {
    return this.params?.value ?? this.params?.data?.statusERP ?? -1;
  }

  get statusText(): string {
    switch (this.statusERP) {
      case OrderStatusERPEnum.ORDER_CREATED:
        return 'Sincronizado';
      case OrderStatusERPEnum.ORDER_PENDING:
        return 'Pendiente';
      default:
        return 'En Proceso';
    }
  }

  get circleClass(): string {
    switch (this.statusERP) {
      case OrderStatusERPEnum.ORDER_CREATED:
        return 'bg-green-500';
      case OrderStatusERPEnum.ORDER_PENDING:
        return 'bg-yellow-500';
      default:
        return 'bg-blue-500';
    }
  }

  get badgeClass(): string {
    switch (this.statusERP) {
      case OrderStatusERPEnum.ORDER_CREATED:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case OrderStatusERPEnum.ORDER_PENDING:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    }
  }
}
